import React, { useState } from 'react';
import { Eye, EyeOff, Lock, Mail, AlertCircle, ArrowRight, ArrowLeft, Store, PlusCircle, ShieldCheck } from 'lucide-react';
import { Button } from '../ui/button';

// Step 3 - Connect Shopify. Existing users enter their Shopify store login (Gmail + password).
// New users give us a fresh Gmail + a password so we build a brand-new store on it.
const ShopifyCredentials = ({ credentials, onCredentialsChange, onNext, onBack }) => {
  const [mode, setMode] = useState(credentials?.mode || 'existing'); // existing | new
  const [email, setEmail] = useState(credentials?.email || '');
  const [password, setPassword] = useState(credentials?.password || '');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});

  const validEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = {};
    if (!email) errs.email = 'Email is required';
    else if (!validEmail(email)) errs.email = 'Enter a valid Gmail / email';
    if (!password) errs.password = 'Password is required';
    else if (password.length < 6) errs.password = 'At least 6 characters';
    setErrors(errs);
    if (Object.keys(errs).length === 0) {
      onCredentialsChange({ mode, email, password });
      onNext();
    }
  };

  return (
    <div className="max-w-md mx-auto">
      {/* Mode toggle */}
      <div className="grid grid-cols-2 gap-2 mb-5">
        <button type="button" onClick={() => setMode('existing')} data-testid="shopify-mode-existing"
          className={`flex items-center gap-2 justify-center py-3 rounded-xl border-2 text-sm font-semibold transition-all ${mode === 'existing' ? 'border-[#5c6ac4] bg-[#f4f6fb] text-[#3f4eae]' : 'border-slate-200 bg-white text-slate-600'}`}>
          <Store className="w-4 h-4" /> I already have a store
        </button>
        <button type="button" onClick={() => setMode('new')} data-testid="shopify-mode-new"
          className={`flex items-center gap-2 justify-center py-3 rounded-xl border-2 text-sm font-semibold transition-all ${mode === 'new' ? 'border-[#5c6ac4] bg-[#f4f6fb] text-[#3f4eae]' : 'border-slate-200 bg-white text-slate-600'}`}>
          <PlusCircle className="w-4 h-4" /> Create a new store
        </button>
      </div>

      {/* Genuine Shopify-style login card */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-2xl overflow-hidden">
        {/* Top brand strip */}
        <div className="bg-[#f6f6f7] border-b border-slate-200 px-6 py-5 flex flex-col items-center">
          {/* Shopify shopping-bag logo mark */}
          <div className="flex items-center gap-2 mb-1">
            <svg viewBox="0 0 448 512" className="w-8 h-8" aria-hidden="true">
              <path fill="#95bf47" d="M388.32 104.1a4.66 4.66 0 0 0-4.4-3.94c-1.72-.15-39.81-2.94-39.81-2.94s-26.4-26.21-29.3-29.1c-2.9-2.9-8.56-2-10.75-1.37-.32.1-5.78 1.78-14.83 4.57-8.9-25.6-24.6-49.14-52.2-49.14h-2.4C227.3 8.1 217.4 0 208.8 0c-66.44 0-98.17 83.05-108.12 125.27-25.82 8-44.15 13.69-46.5 14.42-14.42 4.52-14.87 5-16.76 18.55C35.99 168.5 0 447.24 0 447.24L279.6 512l151.4-32.73S388.5 105.3 388.32 104.1zM261.2 72.72c-7.1 2.2-15.16 4.7-23.9 7.4v-5.2c0-15.87-2.2-28.66-5.74-38.8 14.2 1.78 23.65 17.9 29.64 36.6zm-47-33.55c3.94 9.9 6.5 24.12 6.5 43.32v2.78c-15.57 4.83-32.5 10.06-49.48 15.32 9.54-36.8 27.42-54.6 42.98-61.42zM196.1 24.2a15.4 15.4 0 0 1 8.78 2.96c-20.6 9.7-42.68 34.1-52 82.85-13.6 4.2-26.9 8.33-39.16 12.12C122.99 84.6 149.9 24.2 196.1 24.2z"/>
              <path fill="#5e8e3e" d="M383.92 100.16c-1.72-.15-39.81-2.94-39.81-2.94s-26.4-26.21-29.3-29.1a7.27 7.27 0 0 0-4.1-1.9L279.6 512l151.4-32.73S388.5 105.3 388.32 104.1a4.66 4.66 0 0 0-4.4-3.94z"/>
              <path fill="#fff" d="M234.5 176.9l-18.66 55.5s-16.36-8.73-36.42-8.73c-29.4 0-30.9 18.45-30.9 23.1 0 25.37 66.15 35.1 66.15 94.55 0 46.78-29.66 76.86-69.66 76.86-48 0-72.45-29.87-72.45-29.87l12.85-42.46s25.16 21.6 46.38 21.6c13.86 0 19.5-10.92 19.5-18.9 0-33.1-54.28-34.57-54.28-89 0-45.78 32.86-90.1 99.2-90.1 25.57 0 38.2 7.33 38.2 7.33z"/>
            </svg>
            <span className="text-[22px] font-bold text-[#3d4d31] tracking-tight">shopify</span>
          </div>
          <h2 className="text-lg font-semibold text-slate-800 mt-2">{mode === 'existing' ? 'Log in to your store' : 'Set up your new store'}</h2>
          <p className="text-slate-500 text-xs mt-1 text-center">{mode === 'existing' ? 'Enter your Shopify store login so our team can manage it' : 'Give us a new Gmail + password — we build your store on it'}</p>
        </div>

        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">{mode === 'existing' ? 'Store email / Gmail' : 'New Gmail for the store'}</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><Mail className="h-5 w-5 text-slate-400" /></div>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@gmail.com" data-testid="shopify-email"
                  className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#5c6ac4] focus:border-transparent text-gray-900 ${errors.email ? 'border-red-500' : 'border-slate-300'}`} />
              </div>
              {errors.email && <p className="mt-1 text-xs text-red-500 flex items-center"><AlertCircle className="w-3 h-3 mr-1" />{errors.email}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">{mode === 'existing' ? 'Store password' : 'Set a password'}</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><Lock className="h-5 w-5 text-slate-400" /></div>
                <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter password" data-testid="shopify-password"
                  className={`w-full pl-10 pr-12 py-3 border rounded-lg focus:ring-2 focus:ring-[#5c6ac4] focus:border-transparent text-gray-900 ${errors.password ? 'border-red-500' : 'border-slate-300'}`} />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-500">{showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}</button>
              </div>
              {errors.password && <p className="mt-1 text-xs text-red-500 flex items-center"><AlertCircle className="w-3 h-3 mr-1" />{errors.password}</p>}
            </div>

            <div className="bg-[#f4f6fb] border border-[#dfe3ee] rounded-lg p-3 text-xs text-slate-600 flex items-start">
              <ShieldCheck className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0 text-green-600" />
              <span>Your credentials are encrypted &amp; used only to build and manage your Shopify store for products and ads.</span>
            </div>

            <Button type="submit" data-testid="shopify-submit" className="w-full bg-[#008060] hover:bg-[#006e52] text-white py-3 text-base font-semibold shadow-lg">
              {mode === 'existing' ? 'Log in & Continue' : 'Create & Continue'} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </form>
        </div>
        <div className="px-6 py-3 bg-[#f6f6f7] border-t border-slate-200 text-center">
          <p className="text-[11px] text-slate-400 flex items-center justify-center gap-1"><Lock className="w-3 h-3" /> Secured &amp; encrypted connection</p>
        </div>
      </div>

      <div className="flex items-center justify-start mt-6">
        <Button variant="ghost" onClick={onBack} className="text-slate-500"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
      </div>
    </div>
  );
};

export default ShopifyCredentials;
